import type {
    ConsBusinessFilters, ConsBusinessSortField, SortOrder, KpiMode, CbStatus,
} from '../../types/consolidatedBusiness';

/**
 * Query-string shape for /consolidated-business (design audit 2026-10-07, S3-04): a filtered,
 * sorted, paged view can be shared or reloaded. `open` (the drawer) is a separate, independent
 * key the page itself reads and writes directly, never through this module: it needs its own
 * history entry so the browser's Back button closes the drawer first (Roman, 2026-10-07), where
 * the list state below is only ever replaced — and it must not force a plain `?open=…` link to
 * discard the recipient's own remembered filters (see `RECOGNISED_KEYS`).
 *
 * Only non-default values are written, so an untouched page keeps a bare URL.
 */
export const DEFAULT_FILTERS: ConsBusinessFilters = {};
export const DEFAULT_KPI_MODE: KpiMode = 'net';
export const DEFAULT_SORT_FIELD: ConsBusinessSortField = 'kpiYTD';
export const DEFAULT_SORT_ORDER: SortOrder = 'DESC';

// Named individually, not `keyof ConsBusinessFilters` narrowed by filtering — a key typed that
// broadly turns every read/write below into a union with the boolean/CbStatus fields too, which
// TS then refuses to assign a plain string to.
type StringFilterKey =
    | 'companyCode' | 'businessUnit' | 'salesManager' | 'salesTeam' | 'supplierName'
    | 'categoryName' | 'search' | 'historyMonths' | 'monthsSinceLastInvoice' | 'minSales';

const STRING_FILTER_KEYS: StringFilterKey[] = [
    'companyCode', 'businessUnit', 'salesManager', 'salesTeam', 'supplierName',
    'categoryName', 'search', 'historyMonths', 'monthsSinceLastInvoice', 'minSales',
];

const VALID_STATUS: CbStatus[] = ['Open', 'InProgress', 'Checked', 'Lost'];
const VALID_SORT: ConsBusinessSortField[] = [
    'customerName', 'productName', 'salesManagerName', 'lastInvoice',
    'kpiYTD', 'kpi12m', 'kpiYearAvg', 'avgCycle', 'daysSince',
];
const VALID_KPI: KpiMode[] = ['net', 'quantity', 'grossprofit'];

/**
 * Every LIST-state key this page writes into the query string — used to tell "a deep link that
 * names a filtered/sorted/paged view" from "an unrelated query string" (another app param
 * landing here would not count). `open` is deliberately not one of these: it is read by the page
 * directly and independently of this decision, because "open this one record" and "replace my
 * remembered filters" are different intents — a bare `?open=<groupKey>` link (e.g. from browser
 * history) must still open the drawer without discarding whatever view the recipient already had
 * in sessionStorage.
 */
const RECOGNISED_KEYS = [
    ...STRING_FILTER_KEYS, 'status', 'onlyOverdue', 'hideLost', 'sortBy', 'sortOrder', 'kpiMode', 'page',
];

export interface ConsBusinessListState {
    filters: ConsBusinessFilters;
    sortBy: ConsBusinessSortField;
    sortOrder: SortOrder;
    kpiMode: KpiMode;
    page: number;
}

/** True if the query string carries any state this page recognises — the signal that a deep
 *  link should win over whatever is remembered in sessionStorage (`useConsolidatedBusiness`). */
export function hasListState(params: URLSearchParams): boolean {
    return RECOGNISED_KEYS.some(k => params.has(k));
}

/** Reads list state out of the URL. Anything missing or invalid falls back to `defaults` —
 *  the same tolerance `sanitizeFilters`/`sanitizeSort` apply to a sessionStorage value, because
 *  a hand-edited or stale link is as untrusted as a bundle-upgrade leftover. */
export function readListState(params: URLSearchParams, defaults: ConsBusinessListState): ConsBusinessListState {
    const filters: ConsBusinessFilters = {};
    for (const key of STRING_FILTER_KEYS) {
        const v = params.get(key);
        if (v) filters[key] = v;
    }
    const status = params.get('status');
    if (status && VALID_STATUS.includes(status as CbStatus)) filters.status = status as CbStatus;
    if (params.get('onlyOverdue') === '1') filters.onlyOverdue = true;
    if (params.get('hideLost') === '1') filters.hideLost = true;

    const sortBy = params.get('sortBy');
    const sortOrder = params.get('sortOrder');
    const kpiMode = params.get('kpiMode');
    const page = Number(params.get('page'));

    return {
        filters,
        sortBy: sortBy && VALID_SORT.includes(sortBy as ConsBusinessSortField)
            ? sortBy as ConsBusinessSortField : defaults.sortBy,
        sortOrder: sortOrder === 'ASC' || sortOrder === 'DESC' ? sortOrder : defaults.sortOrder,
        kpiMode: kpiMode && VALID_KPI.includes(kpiMode as KpiMode) ? kpiMode as KpiMode : defaults.kpiMode,
        page: Number.isFinite(page) && page >= 1 ? Math.floor(page) : defaults.page,
    };
}

/** Builds the next query string for the current list state, preserving whatever other params
 *  (namely `open`) are already there. Call with `{ replace: true }` — list-state changes never
 *  get their own history entry, only opening the drawer does. */
export function writeListState(
    params: URLSearchParams, s: ConsBusinessListState, defaults: ConsBusinessListState,
): URLSearchParams {
    const next = new URLSearchParams(params);
    for (const key of STRING_FILTER_KEYS) {
        const v = s.filters[key];
        if (v) next.set(key, v); else next.delete(key);
    }
    if (s.filters.status) next.set('status', s.filters.status); else next.delete('status');
    if (s.filters.onlyOverdue) next.set('onlyOverdue', '1'); else next.delete('onlyOverdue');
    if (s.filters.hideLost) next.set('hideLost', '1'); else next.delete('hideLost');
    if (s.sortBy !== defaults.sortBy) next.set('sortBy', s.sortBy); else next.delete('sortBy');
    if (s.sortOrder !== defaults.sortOrder) next.set('sortOrder', s.sortOrder); else next.delete('sortOrder');
    if (s.kpiMode !== defaults.kpiMode) next.set('kpiMode', s.kpiMode); else next.delete('kpiMode');
    if (s.page > 1) next.set('page', String(s.page)); else next.delete('page');
    return next;
}
